import { request } from './client';
import {
  Word,
  WordDetail,
  WordsListResponse,
  WordPayload,
  FamiliarityPayload,
} from '../types/word';

const PROTECTED_KEYS = new Set([
  'id',
  'user_id',
  'version',
  'created_at',
  'updated_at',
  'deleted_at',
  'cards',
  'familiarity',
  'deck_ids',
  'deck_name',
  'parked',
  'card_ids',
]);

export async function fetchWords(params?: {
  query?: string;
  deck_id?: string;
  limit?: number;
  offset?: number;
  parked?: boolean;
}): Promise<WordsListResponse> {
  const query = new URLSearchParams();
  if (params?.query) query.set('query', params.query);
  if (params?.deck_id) query.set('deck_id', params.deck_id);
  if (params?.limit !== undefined) query.set('limit', String(params.limit));
  if (params?.offset !== undefined) query.set('offset', String(params.offset));
  if (params?.parked !== undefined) query.set('parked', String(params.parked));

  const qs = query.toString();
  return request<WordsListResponse>(`/words${qs ? `?${qs}` : ''}`);
}

export async function fetchWordDetail(id: string): Promise<WordDetail> {
  return request<WordDetail>(`/words/${encodeURIComponent(id)}`);
}

export async function setWordParked(id: string, parked: boolean): Promise<{ word_id: string; parked: boolean; changed_cards: number }> {
  return request(`/words/${encodeURIComponent(id)}/parking`, {
    method: 'POST',
    body: JSON.stringify({ parked }),
  });
}

export async function createWord(payload: WordPayload): Promise<Word> {
  // Strip protected keys if accidentally present
  const clean: Record<string, any> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (!PROTECTED_KEYS.has(k)) {
      clean[k] = v;
    }
  }
  return request<Word>('/words', {
    method: 'POST',
    body: JSON.stringify(clean),
  });
}

export async function updateWord(
  id: string,
  payload: Partial<WordPayload>,
  version?: number
): Promise<Word> {
  // Strip protected keys to avoid backend 422
  const clean: Record<string, any> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (!PROTECTED_KEYS.has(k)) {
      clean[k] = v;
    }
  }

  const headers: Record<string, string> = {};
  if (version !== undefined && version !== null) {
    headers['If-Match'] = `"${version}"`;
  }

  return request<Word>(`/words/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(clean),
  });
}

export async function recordWordFamiliarity(
  id: string,
  payload: FamiliarityPayload
): Promise<{ status?: string; familiarity?: number }> {
  return request<{ status?: string; familiarity?: number }>(
    `/words/${encodeURIComponent(id)}/familiarity`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}
